import { Modal, Button, Group, Stack, Text } from "@mantine/core";
import { IconSend, IconAlertCircle } from "@tabler/icons-react";
import { displayIntentionStatus } from "../utils/display";

export type LinkedInvoiceStatus = {
  externalId: string;
  intentionStatus: string;
};

interface SendEmailIntentionStatusModalProps {
  opened: boolean;
  onClose: () => void;
  linkedInvoices: LinkedInvoiceStatus[];
  onMarkInProgressAndSend: () => void | Promise<void>;
  onSendWithoutStatusChange: () => void | Promise<void>;
  isSending: boolean;
}

export const SendEmailIntentionStatusModal = ({
  opened,
  onClose,
  linkedInvoices,
  onMarkInProgressAndSend,
  onSendWithoutStatusChange,
  isSending,
}: SendEmailIntentionStatusModalProps) => {
  const handleClose = () => {
    if (isSending) return;
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      closeOnClickOutside={!isSending}
      closeOnEscape={!isSending}
      withCloseButton={!isSending}
      title={
        <Group gap="xs">
          <IconAlertCircle size={20} />
          <Text size="lg" fw={600}>
            Update invoice status?
          </Text>
        </Group>
      }
      radius="md"
      padding="xl"
      styles={{
        content: {
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Sending an email does not always resolve the issue. For example, if a
          customer replies that an invoice is under inspection, the invoice may
          still need attention.
        </Text>

        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 shadow-inner">
          <Text size="sm" fw={500} c="dimmed" mb="sm">
            Linked invoice{linkedInvoices.length > 1 ? "s" : ""}
          </Text>
          <Stack gap="xs">
            {linkedInvoices.map((invoice) => (
              <Group key={invoice.externalId} justify="space-between">
                <Text size="sm" fw={600}>
                  {invoice.externalId}
                </Text>
                <Text size="sm" c="dimmed">
                  {displayIntentionStatus(invoice.intentionStatus) ??
                    invoice.intentionStatus}
                </Text>
              </Group>
            ))}
          </Stack>
        </div>

        <Text size="sm">
          Would you like to mark{" "}
          {linkedInvoices.length > 1 ? "these invoices" : "this invoice"} as In
          Progress, or keep the current status?
        </Text>

        <div className="mt-6 flex flex-col gap-2">
          <Button
            fullWidth
            onClick={() => void onMarkInProgressAndSend()}
            loading={isSending}
            color="blue"
            radius="md"
            leftSection={<IconSend size={16} />}
          >
            Mark as In Progress &amp; Send
          </Button>
          <Button
            fullWidth
            variant="outline"
            radius="md"
            onClick={() => void onSendWithoutStatusChange()}
            loading={isSending}
            disabled={isSending}
          >
            Send without changing status
          </Button>
          <Button
            variant="subtle"
            color="gray"
            radius="md"
            className="self-start"
            onClick={handleClose}
            disabled={isSending}
          >
            Cancel
          </Button>
        </div>
      </Stack>
    </Modal>
  );
};
